import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'

import { cargarGoogleMaps, hayLlaveDeGoogle, ESTILO_MAPA } from '@/lib/mapas/google'
import { UbicarPendientes } from '@/modules/mapa-calor/UbicarPendientes'
import { useGuardarUbicacionManual } from '@/modules/mapa-calor/useUbicarOrdenes'
import { useCronograma } from '@/modules/tablero/useCronograma'
import { useColaboradoresActivos } from '@/modules/programacion/useHerramientas'
import { pesos } from '@/shared/utilidades/formato'

import type { OrdenServicioConRelaciones } from './tipos'
import estilos from './MapaOrdenes.module.css'

/**
 * LAS ÓRDENES EN EL MAPA.
 *
 * La matriz responde «qué hay»; el mapa responde «DÓNDE está el trabajo», que
 * es la pregunta de quien reparte técnicos por la mañana. Dos órdenes en el
 * mismo barrio se ven pegadas y se mandan juntas.
 *
 * EL COLOR ES EL ESTADO EFECTIVO, no solo el de la orden de servicio. Los
 * estados de programación —pendiente de programar, programada, en ejecución—
 * viven en la orden de TRABAJO, no en la de servicio, así que aquí se combinan:
 * una orden aprobada sin técnico sale como «pendiente de programación», y una
 * que el técnico ya empezó sale «en ejecución», más grande. Así programación
 * ve las zonas que le faltan y enruta al mismo técnico por un mismo sector; y
 * desde el punto pendiente puede programar sin salir del mapa.
 */

/** El estado efectivo de una orden, combinando servicio + trabajo. */
type EstadoEfectivo =
  | 'creada'
  | 'pendiente_cotizar'
  | 'cotizada'
  | 'pendiente_programacion'
  | 'programada'
  | 'en_ejecucion'
  | 'pendiente_firma'
  | 'finalizada'
  | 'anulada'

/** Color y nombre de cada estado efectivo, en el orden del recorrido. */
const ESTADOS: { clave: EstadoEfectivo; etiqueta: string; color: string }[] = [
  { clave: 'creada', etiqueta: 'Creada', color: '#77709A' },
  { clave: 'pendiente_cotizar', etiqueta: 'Por cotizar', color: '#2D0177' },
  { clave: 'cotizada', etiqueta: 'Cotizada', color: '#FFAB00' },
  { clave: 'pendiente_programacion', etiqueta: 'Por programar', color: '#0EA5E9' },
  { clave: 'programada', etiqueta: 'Programada', color: '#16A34A' },
  { clave: 'en_ejecucion', etiqueta: 'En ejecución', color: '#DB2777' },
  { clave: 'pendiente_firma', etiqueta: 'Esperando firma', color: '#7C3AED' },
  { clave: 'finalizada', etiqueta: 'Cerrada', color: '#64748B' },
  { clave: 'anulada', etiqueta: 'Anulada', color: '#C2410C' },
]
const COLOR = Object.fromEntries(ESTADOS.map((e) => [e.clave, e.color])) as Record<
  EstadoEfectivo,
  string
>
const ETIQUETA = Object.fromEntries(ESTADOS.map((e) => [e.clave, e.etiqueta])) as Record<
  EstadoEfectivo,
  string
>

/**
 * EL DIBUJO DE CADA ESTADO.
 *
 * El color solo no alcanza: ocho colores en un mapa se confunden, y a quien no
 * los separa bien le da lo mismo el naranja que el rosa. El glifo dice de qué
 * se trata sin ir a la leyenda —un calendario es programación, un rayo es
 * trabajo en curso, una pluma es firma— y sobrevive a la fotocopia.
 */
const GLIFO: Record<EstadoEfectivo, string> = {
  // Una hoja: la orden de servicio recién escrita, sin precio y sin fecha.
  creada: '<path d="M6.5 2.8h7l4.2 4.2V21.2H6.5z"/><path d="M13.5 2.8v4.4h4.2"/>',
  pendiente_cotizar:
    '<path d="M20 12.5V4h-8.5L4 11.5 12.5 20z"/><circle cx="15.8" cy="8.2" r="1.3"/>',
  cotizada: '<rect x="3" y="7" width="18" height="10" rx="2.5"/><circle cx="12" cy="12" r="2.3"/>',
  pendiente_programacion:
    '<rect x="4" y="5.5" width="16" height="14.5" rx="2.5"/><path d="M4 10.5h16M8.5 3v4M15.5 3v4"/>',
  programada:
    '<rect x="4" y="5.5" width="16" height="14.5" rx="2.5"/><path d="M4 10.5h16M8.5 3v4M15.5 3v4M9 15l2.2 2.2L15.5 13"/>',
  en_ejecucion: '<path d="M13.5 2.5L5 13h5.5L9.5 21.5 19 11h-5.5z"/>',
  pendiente_firma: '<path d="M3.5 20.5l4.2-1.1 10-10-3.1-3.1-10 10z"/><path d="M14 6l3.1 3.1M3 22h18"/>',
  // No se pintan nunca (ver FUERA_DEL_MAPA), pero el tipo los exige completos.
  finalizada: '<path d="M6 12.5l4 4 8-9"/>',
  anulada: '<path d="M7 7l10 10M17 7L7 17"/>',
}

/**
 * LO QUE NO VA AL MAPA.
 *
 * El mapa responde «dónde está el trabajo», en presente. Una orden cerrada y
 * una anulada no son trabajo: son historia. Dejarlas puestas llena el mapa de
 * puntos sobre los que no hay nada que hacer y esconde entre ellos los cuatro
 * que sí importan esta mañana. Quien las necesite las busca en la matriz, que
 * es donde se consulta lo que ya pasó.
 */
const FUERA_DEL_MAPA: EstadoEfectivo[] = ['finalizada', 'anulada']

/** Centro de Cartago: donde MEGA trabaja, por si no hay ni una orden ubicada. */
const CARTAGO = { lat: 4.7465, lng: -75.9115 }

/**
 * LOS COLORES DE LAS RUTAS, uno por técnico.
 *
 * La línea une las paradas de un técnico en el día, en orden de hora. El color
 * no dice estado —eso lo dice el punto—: dice DE QUIÉN es el recorrido, para
 * seguir con la vista por dónde anda cada uno sin cruzar dos rutas. Se eligen
 * saturados y distintos entre sí; si hay más técnicos que colores, se repiten,
 * que es raro y mejor que inventar tonos que no se distinguen.
 */
const PALETA_RUTA = [
  '#2563EB', '#DB2777', '#059669', '#D97706',
  '#7C3AED', '#0891B2', '#DC2626', '#4D7C0F',
]

/**
 * Combina orden de servicio + orden de trabajo en un solo estado para el color.
 * Si hay orden de trabajo, ella manda (ya está aprobada y en el flujo de campo);
 * si no, vale el estado de la orden de servicio.
 */
function estadoEfectivo(orden: OrdenServicioConRelaciones): EstadoEfectivo {
  if (orden.estado === 'anulada') return 'anulada'

  const ot = orden.orden_trabajo
  if (ot) {
    switch (ot.estado) {
      case 'creada':
        return 'pendiente_programacion'
      case 'programada':
        // Programada pero sin técnico o sin fecha: todavía hay que programarla.
        return ot.colaborador_id && ot.fecha_inicio_programada
          ? 'programada'
          : 'pendiente_programacion'
      case 'en_ejecucion':
        return 'en_ejecucion'
      // El precierre es el trabajo hecho esperando que el cliente firme el
      // acta. No es ejecución —ahí ya nadie está trabajando— y no es cierre
      // —todavía falta la firma—: es una espera que hay que perseguir, y si no
      // se ve en el mapa nadie se acuerda de llamar al que lleva tres días.
      case 'en_precierre':
        return 'pendiente_firma'
      case 'cerrada':
      case 'finalizada':
        return 'finalizada'
    }
  }

  if (orden.estado === 'creada') return 'creada'
  if (orden.estado === 'pendiente_cotizar') return 'pendiente_cotizar'

  // UNA COTIZACIÓN PROVISIONAL NO ES UNA COTIZACIÓN.
  //
  // El estado de la orden dice «cotizada» en cuanto existe un documento, y las
  // veintitrés que entraron por el Excel traen uno provisional —«PROV-…», sin
  // nada de World Office detrás y con valor cero—. El mapa las pintaba a todas
  // de «Cotizada», que afirma que tienen precio, y así no había forma de ver
  // en el mapa cuáles son las que están esperando que alguien se lo ponga:
  // justo lo que se va a mirar cuando se sale a cotizar por una zona.
  //
  // `wo_id_asiento` en null es el dato estructural: no hay documento de verdad.
  if (!orden.cotizacion || orden.cotizacion.wo_id_asiento === null) return 'pendiente_cotizar'

  return 'cotizada'
}

/**
 * UN PUNTO DEL MAPA, DIBUJADO POR NOSOTROS.
 *
 * google.maps.Marker solo sabe pintar una forma de su catálogo o una imagen
 * plana: no admite HTML dentro, y sin HTML no hay glifo ni animación de verdad
 * —lo anterior era un setInterval saltando entre dos tamaños, que es un
 * parpadeo, no un latido—. Un OverlayView sí: se le da un trozo de DOM y el
 * mapa se encarga de ponerlo donde va en cada desplazamiento y cada zoom.
 *
 * La clase se construye la primera vez que hace falta y no al importar el
 * módulo, porque extiende una clase que solo existe DESPUÉS de que la API de
 * Google haya cargado. Escribirla arriba del archivo reventaría al importarlo.
 */
type ClasePunto = new (
  posicion: google.maps.LatLngLiteral,
  color: string,
  glifo: string,
  late: boolean,
  titulo: string,
) => google.maps.OverlayView & {
  alTocar: (fn: () => void) => void
  alAbrir: (fn: () => void) => void
  /** Solo los que aceptan técnico: los pendientes de programación. */
  aceptarTecnico: (fn: (colaboradorId: string) => void) => void
  /** Mover el punto a mano, arrastrándolo por el mapa. */
  alMover: (fn: (lat: number, lon: number) => void) => void
}

let claseGuardada: ClasePunto | null = null

function clasePunto(maps: typeof google.maps): ClasePunto {
  if (claseGuardada) return claseGuardada

  class PuntoOrden extends maps.OverlayView {
    private caja: HTMLDivElement | null = null
    private tocar: (() => void) | null = null
    private abrir: (() => void) | null = null
    private soltar: ((colaboradorId: string) => void) | null = null
    private mover: ((lat: number, lon: number) => void) | null = null
    private dianaPuesta = false
    private arrastrePuesto = false

    constructor(
      private readonly posicion: google.maps.LatLngLiteral,
      private readonly color: string,
      private readonly glifo: string,
      private readonly late: boolean,
      private readonly titulo: string,
    ) {
      super()
    }

    alTocar(fn: () => void) {
      this.tocar = fn
    }

    alAbrir(fn: () => void) {
      this.abrir = fn
    }

    /**
     * ARRASTRAR EL TÉCNICO A LA ORDEN, y no al revés.
     *
     * En un mapa la orden está clavada a una dirección real: no se mueve. Lo
     * que se mueve es la persona. Por eso el gesto va en este sentido, que
     * además es el que se hace con el dedo sobre un plano cuando se reparte
     * trabajo de verdad.
     *
     * Solo lo llaman los puntos que de verdad pueden recibir a alguien —los
     * pendientes de programación—: un punto que se ilumina y luego no acepta
     * nada enseña a desconfiar del resto.
     */
    /**
     * MOVER EL PUNTO A MANO.
     *
     * Google acierta la mayoría, pero en Cartago hay direcciones que no
     * existen para él —«Villa Hermosa, casa modelo», «zona rural de Balboa»—
     * y ahí devuelve el centro del municipio, que el sistema descarta. La
     * salida es ponerlo donde de verdad está, y el sitio natural para hacerlo
     * es el mapa, no un formulario en otra pantalla.
     *
     * Se guarda la intención aunque la caja no exista todavía, por lo mismo
     * que `aceptarTecnico`: `setMap()` no crea el DOM en el acto.
     */
    alMover(fn: (lat: number, lon: number) => void) {
      this.mover = fn
      this.armarArrastre()
    }

    /**
     * El arrastre, con tres cuidados que no son adorno:
     *
     *  · UN UMBRAL. Sin él, cualquier clic con un temblor de dos píxeles
     *    movería la orden a otra esquina. Hasta que no se recorren seis no
     *    empieza a contar como arrastre, así que el clic de siempre —abrir la
     *    ficha— sigue funcionando igual.
     *  · EL MAPA SE QUEDA QUIETO mientras se mueve un punto. Si no, se
     *    arrastran los dos a la vez y el punto acaba donde nadie quiso.
     *  · SE SUELTA DONDE ESTÁ EL CURSOR. La caja se dibuja centrada en su
     *    coordenada, así que se convierte la posición del ratón, no la
     *    esquina de la caja.
     */
    private armarArrastre() {
      if (!this.caja || !this.mover || this.arrastrePuesto) return
      this.arrastrePuesto = true

      const caja = this.caja
      caja.classList.add(estilos.movible)

      caja.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return
        e.stopPropagation()

        const inicio = { x: e.clientX, y: e.clientY }
        let arrastrando = false

        const mapa = this.getMap() as google.maps.Map | undefined

        const alMoverRaton = (m: MouseEvent) => {
          const recorrido = Math.hypot(m.clientX - inicio.x, m.clientY - inicio.y)
          if (!arrastrando && recorrido < 6) return

          if (!arrastrando) {
            arrastrando = true
            mapa?.setOptions({ draggable: false })
            caja.classList.add(estilos.moviendo)
          }

          // Se mueve la caja con el ratón para que se vea a dónde va.
          const div = this.getPanes()?.overlayMouseTarget
          const marco = div?.parentElement?.getBoundingClientRect()
          if (!marco) return
          caja.style.left = String(m.clientX - marco.left) + 'px'
          caja.style.top = String(m.clientY - marco.top) + 'px'
        }

        const alSoltar = (m: MouseEvent) => {
          window.removeEventListener('mousemove', alMoverRaton)
          window.removeEventListener('mouseup', alSoltar)

          if (!arrastrando) return

          mapa?.setOptions({ draggable: true })
          caja.classList.remove(estilos.moviendo)

          const div = this.getPanes()?.overlayMouseTarget
          const marco = div?.parentElement?.getBoundingClientRect()
          const proj = this.getProjection()
          if (!marco || !proj) return

          /*
           * DE VUELTA POR LA MISMA PUERTA POR LA QUE SE ENTRÓ.
           *
           * Google maneja DOS sistemas de píxeles: los del CONTENEDOR —desde
           * la esquina visible del mapa— y los del DIV de overlays, que va
           * desplazado y se mueve con el mapa. `draw()` coloca esta caja con
           * `fromLatLngToDivPixel`, y aquí arriba el arrastre la sigue
           * moviendo en ese mismo sistema… pero al soltar se convertía con
           * `fromContainerPixelToLatLng`, que es el otro.
           *
           * Los dos coinciden solo mientras el mapa no se haya desplazado
           * nunca; en cuanto alguien lo mueve un poco, el punto aterriza
           * corrido por esa diferencia. De ahí el «lo suelto aquí y me salta
           * a otro lado», y de ahí también que a veces pareciera funcionar.
           */
          const punto = proj.fromDivPixelToLatLng(
            new maps.Point(m.clientX - marco.left, m.clientY - marco.top),
          )
          if (punto) this.mover?.(punto.lat(), punto.lng())
        }

        window.addEventListener('mousemove', alMoverRaton)
        window.addEventListener('mouseup', alSoltar)
      })
    }

    aceptarTecnico(fn: (colaboradorId: string) => void) {
      this.soltar = fn
      // SE GUARDA LA INTENCIÓN Y SE ARMA CUANDO SE PUEDA.
      //
      // Aquí antes había un `if (!this.caja) return`, y esa línea rompía la
      // función entera. `setMap()` no crea el DOM del punto en el acto: Google
      // llama a `onAdd()` en su propio momento. Como esto se invoca justo
      // después de `setMap()`, casi siempre la caja todavía no existía, así que
      // se salía sin poner el resaltado NI los escuchadores: arrastrar el
      // técnico encima no hacía nada.
      //
      // Y como era una carrera, alguno sí llegaba a tiempo —de ahí que se
      // viera UN punto resaltado y los demás no, que parecía un filtro raro y
      // era este fallo.
      this.armarDiana()
    }

    /** Pone el resaltado y los escuchadores. Idempotente: puede llamarse desde
     *  `aceptarTecnico` o desde `onAdd`, según quién llegue primero. */
    private armarDiana() {
      if (!this.caja || !this.soltar || this.dianaPuesta) return
      this.dianaPuesta = true

      this.caja.classList.add(estilos.diana)
      this.caja.addEventListener('dragover', (e) => {
        e.preventDefault()
        // Sin esto el navegador no considera la caja un destino válido y el
        // cursor muestra la señal de prohibido durante todo el arrastre.
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
        this.caja?.classList.add(estilos.dianaViva)
      })
      this.caja.addEventListener('dragleave', () => {
        this.caja?.classList.remove(estilos.dianaViva)
      })
      this.caja.addEventListener('drop', (e) => {
        e.preventDefault()
        this.caja?.classList.remove(estilos.dianaViva)
        const quien = e.dataTransfer?.getData('text/colaborador')
        if (quien) this.soltar?.(quien)
      })
    }

    override onAdd() {
      const caja = document.createElement('div')
      caja.className = estilos.marcador
      caja.title = this.titulo
      caja.style.setProperty('--color-punto', this.color)
      caja.innerHTML = `
        ${this.late ? `<span class="${estilos.onda}"></span><span class="${estilos.onda2}"></span>` : ''}
        <span class="${estilos.nucleo}">
          <svg viewBox="0 0 24 24" aria-hidden="true">${this.glifo}</svg>
        </span>`

      caja.addEventListener('click', () => this.tocar?.())
      caja.addEventListener('dblclick', (e) => {
        // Sin esto el doble clic también lo recoge el mapa y hace zoom, así que
        // abrir la orden dejaba el mapa saltando de escala por debajo.
        e.stopPropagation()
        this.abrir?.()
      })

      // overlayMouseTarget es el único panel que recibe eventos de ratón: en
      // floatPane el punto se ve pero no se puede tocar.
      this.getPanes()?.overlayMouseTarget.appendChild(caja)
      this.caja = caja

      // Si ya nos habían dicho que este punto acepta técnicos —o que se
      // puede mover— antes de que existiera la caja, es AHORA cuando se arma.
      this.armarDiana()
      this.armarArrastre()
    }

    override draw() {
      if (!this.caja) return
      const punto = this.getProjection()?.fromLatLngToDivPixel(new maps.LatLng(this.posicion))
      if (!punto) return
      this.caja.style.left = `${punto.x}px`
      this.caja.style.top = `${punto.y}px`
    }

    override onRemove() {
      this.caja?.remove()
      this.caja = null
      // La caja siguiente es otra: sus escuchadores hay que ponerlos de nuevo.
      this.dianaPuesta = false
      this.arrastrePuesto = false
    }
  }

  claseGuardada = PuntoOrden as unknown as ClasePunto
  return claseGuardada
}

/**
 * UN OVERLAY INVISIBLE, SOLO PARA LA PROYECCIÓN.
 *
 * Para saber a qué coordenada corresponde el sitio donde se soltó una orden
 * hace falta la proyección del mapa —convertir píxeles del contenedor a
 * latitud/longitud—, y esa solo la da un `OverlayView` vivo sobre el mapa. Los
 * puntos de las órdenes nacen y mueren con cada filtro, así que no sirven de
 * ancla; este vive fijo desde que el mapa existe y no dibuja nada. Igual que
 * `clasePunto`, se construye tarde porque `OverlayView` no existe hasta que
 * Google ha cargado.
 */
type ClaseProyeccion = new () => google.maps.OverlayView
let claseProyGuardada: ClaseProyeccion | null = null

function claseProyeccion(maps: typeof google.maps): ClaseProyeccion {
  if (claseProyGuardada) return claseProyGuardada
  class Proyeccion extends maps.OverlayView {
    override onAdd() {}
    override draw() {}
    override onRemove() {}
  }
  claseProyGuardada = Proyeccion as unknown as ClaseProyeccion
  return claseProyGuardada
}

type Props = {
  ordenes: OrdenServicioConRelaciones[]
  onAbrir: (ordenId: string) => void
  /** Abre el diálogo de programar para una orden pendiente de programación. */
  /**
   * Sin esto el mapa solo SE MIRA.
   *
   * Programar vivía en tres sitios a la vez —el riel de reparto, este mapa y
   * la ficha de la orden— y tres caminos para lo mismo no son tres
   * comodidades: son tres sitios donde comprobar cuando algo sale mal, y tres
   * versiones de la misma regla que se van separando.
   *
   * Desde que el mapa vive DENTRO del recorrido, su modo de programar repetía
   * al del reparto sin añadir nada: el reparto tiene el eje del tiempo, que es
   * lo que hace falta para decir a qué hora. Aquí se contesta «dónde está el
   * trabajo» y nada más.
   */
  onProgramar?: (orden: OrdenServicioConRelaciones, colaboradorId?: string, fecha?: string) => void
}

/**
 * Quién hay y qué tan cargado está ese día.
 *
 * Es el dato que decide a quién se le puede meter otra: un nombre suelto no
 * sirve para repartir. Sale de lo que ya existe —los colaboradores activos y
 * el cronograma— sin consultar nada nuevo.
 */
function useCargaDelDia(fecha: string) {
  const { colaboradores } = useColaboradoresActivos()
  const { filas } = useCronograma()

  return useMemo(() => {
    const horas = new Map<string, { horas: number; trabajos: number }>()
    for (const f of filas) {
      if (!f.colaborador_id) continue
      if ((f.fecha_inicio_programada ?? '').slice(0, 10) !== fecha) continue
      const y = horas.get(f.colaborador_id) ?? { horas: 0, trabajos: 0 }
      y.horas += Number(f.horas_asignadas ?? 0)
      y.trabajos += 1
      horas.set(f.colaborador_id, y)
    }

    return {
      cargaDelDia: colaboradores.map((c) => ({
        ...c,
        horas: horas.get(c.id)?.horas ?? 0,
        trabajos: horas.get(c.id)?.trabajos ?? 0,
      })),
    }
  }, [colaboradores, filas, fecha])
}

const hoyIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * El día local (YYYY-MM-DD) de un instante ISO, o null si no hay.
 *
 * `fecha_inicio_programada` se guarda en UTC —Programar arma el ISO con
 * `toISOString()`—; aquí se convierte a la hora del navegador, la de la oficina
 * en Colombia, para que «ese día» sea el día que se ve en el calendario y no el
 * de Greenwich, que en la noche ya va por el siguiente.
 */
function diaLocalDe(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function MapaOrdenes({ ordenes, onAbrir, onProgramar }: Props) {
  /**
   * MODO PROGRAMACIÓN.
   *
   * El mapa deja de ser solo una lectura y pasa a ser la mesa donde se reparte:
   * se arrastra un técnico sobre una orden sin programar y se acuerda la hora.
   *
   * El resto de los puntos NO SE ESCONDE, se apaga. Ver las cotizaciones y lo
   * que está en ejecución mientras se reparte es justamente lo que hace útil
   * repartir sobre un plano: se agrupa por sector con lo que ya hay allá.
   */
  const [programando, setProgramando] = useState(false)
  // Sin quien reciba la programación, el modo no existe.
  const puedeProgramarAqui = Boolean(onProgramar)
  const [diaElegido, setDiaElegido] = useState(hoyIso())
  const { cargaDelDia } = useCargaDelDia(diaElegido)
  const caja = useRef<HTMLDivElement>(null)
  const mapa = useRef<google.maps.Map | null>(null)
  const marcas = useRef<google.maps.OverlayView[]>([])
  // Las líneas de ruta, una por técnico. Se limpian y se vuelven a trazar en
  // cada redibujado, igual que los puntos.
  const lineas = useRef<google.maps.Polyline[]>([])
  // El overlay invisible que presta su proyección para soltar órdenes a mano.
  const proyeccion = useRef<google.maps.OverlayView | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Verdadero mientras se arrastra una orden «sin ubicar»: enciende el borde
  // del mapa para que se vea que ahí es donde se suelta.
  const [arrastrando, setArrastrando] = useState(false)

  // Poner a mano el punto de una orden que Google no encontró: se arrastra
  // desde la columna de la izquierda y se suelta sobre el sitio exacto.
  const { guardar, guardando, error: errorUbicar } = useGuardarUbicacionManual()
  // La leyenda ES el filtro, y es INTERNO al mapa: filtra los puntos que se ven
  // por estado efectivo, sin tocar el filtro de la tabla.
  const [filtro, setFiltro] = useState<EstadoEfectivo | 'todos'>('todos')
  // El filtro por día: null = todos los días, como estaba. Con un día puesto,
  // solo quedan las órdenes programadas para ESE día —el plan de esa mañana—;
  // las que aún no tienen fecha (por cotizar, por programar) no son de ningún
  // día y se ocultan mientras se mira uno concreto.
  const [diaVista, setDiaVista] = useState<string | null>(null)
  // Y de quién. 'todos' = el mapa general, como estaba. Con un técnico puesto,
  // el mapa queda con SU recorrido y nada más, que es la pregunta de quien
  // abre esta pantalla por la mañana: por dónde anda cada uno.
  const [tecnicoVista, setTecnicoVista] = useState<string>('todos')

  /*
   * DOS MAPAS, NO UNO CON TRES FILTROS ENCIMA.
   *
   * Johan: «el mapa puede ser general o para ver la ruta de técnicos, ahí
   * estás mezclando todo». Tenía razón. Son dos preguntas distintas y cada una
   * quiere ver otra cosa:
   *
   *   · GENERAL — dónde está cada trabajo y en qué estado. Manda el color por
   *     estado, la columna de las que Google no ubicó y el programar
   *     arrastrando. No tiene nada que ver con un día.
   *   · RUTAS DEL DÍA — por dónde anda cada técnico esa jornada. Manda el día,
   *     el nombre y la línea. El estado del documento aquí no pinta nada.
   *
   * Apiladas sobre el mismo encabezado, cada una estorbaba a la otra.
   */
  const [modo, setModo] = useState<'general' | 'rutas' | 'finalizados'>('general')

  /** Hoy, en local, para arrancar el modo rutas con algo dibujado. */
  const hoyLocal = () => {
    const d = new Date()
    const mes = String(d.getMonth() + 1).padStart(2, '0')
    const dia = String(d.getDate()).padStart(2, '0')
    return d.getFullYear() + '-' + mes + '-' + dia
  }

  function cambiarModo(nuevo: 'general' | 'rutas' | 'finalizados') {
    setModo(nuevo)

    if (nuevo === 'finalizados') {
      // Lo terminado no es de un día ni de un técnico: es el archivo.
      setDiaVista(null)
      setTecnicoVista('todos')
      setFiltro('todos')
      return
    }

    if (nuevo === 'rutas') {
      // Sin día no hay ruta, así que se entra por hoy en vez de por un mapa
      // vacío que obliga a adivinar qué falta.
      setDiaVista((d) => d ?? hoyLocal())
      setFiltro('todos')
    } else {
      // Volver a general es volver al mapa de siempre: sin día y sin técnico.
      setDiaVista(null)
      setTecnicoVista('todos')
    }
  }

  // onProgramar cambia de identidad en cada render del padre; se guarda en una
  // referencia para no reconstruir el mapa entero cada vez.
  const onProgramarRef = useRef(onProgramar)
  onProgramarRef.current = onProgramar
  const onAbrirRef = useRef(onAbrir)
  onAbrirRef.current = onAbrir

  // El modo y el guardado, en referencias: el efecto que dibuja el mapa no
  // debe rehacerse porque cambie una de estas dos cosas.
  const modoRef = useRef(modo)
  modoRef.current = modo
  const guardarRef = useRef(guardar)
  guardarRef.current = guardar

  /*
   * MOVER PUNTOS ES UN MODO, NO ALGO QUE ESTÁ SIEMPRE PUESTO.
   *
   * Johan: «me deja mover las órdenes en el mapa muy libremente, mejor que
   * tenga la opción mover y de ahí sí». Tiene razón: un mapa es para mirarlo
   * y el clic natural sobre un punto es abrirlo, no arrastrarlo. Con el
   * arrastre siempre activo, el umbral de seis píxeles es lo único que separa
   * consultar de cambiar el sitio de una orden.
   *
   * Así que se entra a propósito, como en «Programar sobre el mapa».
   */
  const [moviendoPuntos, setMoviendoPuntos] = useState(false)

  /** Qué vista se encuadró la última vez, para no repetirlo en cada refresco. */
  const encuadrado = useRef<string>('')

  /** La última que se movió a mano, para poder deshacerlo. */
  const [movida, setMovida] = useState<{
    id: string
    numero: string
    lat: number
    lon: number
  } | null>(null)

  // Cada orden con su estado efectivo y su punto, una sola vez.
  const conEstado = useMemo(
    () =>
      ordenes
        .map((o) => ({
          orden: o,
          efectivo: estadoEfectivo(o),
          lat: (o as unknown as { latitud: number | null }).latitud,
          lon: (o as unknown as { longitud: number | null }).longitud,
        }))
        // Se descartan aquí y no al pintar: así tampoco salen en la leyenda ni
        // se cuentan entre las «sin ubicar», que es donde más estorbaban —una
        // orden anulada de hace tres meses no es una ubicación que falte.
        //
        // Salvo en el modo de finalizados, que es justo al revés: ahí lo único
        // que interesa es lo terminado. Johan quitó el mapa de calor y pidió
        // que esto viviera aquí, en el mapa que ya se usa.
        .filter((c) =>
          modo === 'finalizados'
            ? c.efectivo === 'finalizada'
            : !FUERA_DEL_MAPA.includes(c.efectivo),
        ),
    [ordenes, modo],
  )

  // Cuáles estados hay presentes, para no mostrar en la leyenda colores que
  // no corresponden a ningún punto.
  const presentes = useMemo(() => new Set(conEstado.map((c) => c.efectivo)), [conEstado])

  const { ubicadas, sinUbicar } = useMemo(() => {
    const con: typeof conEstado = []
    const sin: typeof conEstado = []
    for (const c of conEstado) {
      const visible = filtro === 'todos' || c.efectivo === filtro
      if (!visible) continue
      // El día: si se está mirando uno, solo entran las programadas para él.
      if (diaVista != null && diaLocalDe(c.orden.orden_trabajo?.fecha_inicio_programada) !== diaVista)
        continue
      // Y el técnico: con uno escogido, lo demás estorba. Una orden sin
      // asignar tampoco es suya, así que también se va.
      if (tecnicoVista !== 'todos' && c.orden.orden_trabajo?.colaborador_id !== tecnicoVista)
        continue
      if (c.lat != null && c.lon != null) con.push(c)
      else sin.push(c)
    }
    return { ubicadas: con, sinUbicar: sin }
  }, [conEstado, filtro, diaVista, tecnicoVista])

  /**
   * A QUIÉN SE PUEDE ESCOGER.
   *
   * Los técnicos que tienen trabajo en lo que se está mirando, no la plantilla
   * entera: una lista con veinte nombres de los que solo tres salieron hoy
   * obliga a buscar. Se calcula ANTES del filtro por técnico —si no, al
   * escoger uno el desplegable se quedaría con un solo nombre y no habría
   * forma de cambiar—.
   */
  const nombrePorTecnico = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of conEstado) {
      const ot = c.orden.orden_trabajo
      if (ot?.colaborador_id) m.set(ot.colaborador_id, ot.colaborador?.nombre ?? 'Técnico')
    }
    return m
  }, [conEstado])

  const tecnicosALaVista = useMemo(() => {
    const porId = new Map<string, string>()

    for (const c of conEstado) {
      const ot = c.orden.orden_trabajo
      if (!ot?.colaborador_id) continue
      if (diaVista != null && diaLocalDe(ot.fecha_inicio_programada) !== diaVista) continue
      porId.set(ot.colaborador_id, ot.colaborador?.nombre ?? 'Técnico')
    }

    return [...porId].map(([id, nombre]) => ({ id, nombre })).sort((a, b) =>
      a.nombre.localeCompare(b.nombre),
    )
  }, [conEstado, diaVista])

  /**
   * LAS RUTAS DEL DÍA, una por técnico.
   *
   * Solo cuando se está mirando UN día: unir paradas de días distintos no es un
   * recorrido, es una maraña. Se agrupan las órdenes ubicadas por técnico, se
   * ordenan por la hora de inicio y se arma la línea. Con una sola parada no hay
   * nada que unir, así que esas no cuentan como ruta —el punto ya se ve solo—.
   */
  const rutas = useMemo(() => {
    if (diaVista == null) return []

    const porTecnico = new Map<string, { nombre: string; paradas: typeof ubicadas }>()
    for (const c of ubicadas) {
      const ot = c.orden.orden_trabajo
      if (!ot?.colaborador_id || c.lat == null || c.lon == null) continue
      const g = porTecnico.get(ot.colaborador_id) ?? {
        nombre: ot.colaborador?.nombre ?? 'Técnico',
        paradas: [] as typeof ubicadas,
      }
      g.paradas.push(c)
      porTecnico.set(ot.colaborador_id, g)
    }

    const lista: {
      id: string
      nombre: string
      color: string
      puntos: google.maps.LatLngLiteral[]
    }[] = []
    let i = 0
    for (const [id, g] of porTecnico) {
      if (g.paradas.length < 2) continue
      const ordenadas = [...g.paradas].sort((a, b) =>
        (a.orden.orden_trabajo?.fecha_inicio_programada ?? '').localeCompare(
          b.orden.orden_trabajo?.fecha_inicio_programada ?? '',
        ),
      )
      lista.push({
        id,
        nombre: g.nombre,
        color: PALETA_RUTA[i % PALETA_RUTA.length],
        puntos: ordenadas.map((p) => ({ lat: Number(p.lat), lng: Number(p.lon) })),
      })
      i++
    }
    return lista
  }, [ubicadas, diaVista])

  /**
   * LO QUE HAY QUE DECIRLE A QUIEN ESCOGIÓ UN TÉCNICO Y NO VE NINGUNA LÍNEA.
   *
   * Son cuatro situaciones distintas y solo una es «está todo bien»:
   * falta el día, ese día no trabajó, trabajó pero en un solo sitio —una
   * parada no es un recorrido— o sí hay ruta y entonces no hay nada que
   * explicar. Un mapa en blanco las cuenta todas igual.
   */
  const porQueNoHayRuta = useMemo(() => {
    if (modo !== 'rutas') return ''
    if (diaVista == null) return 'Escoja un día para ver las rutas'

    const cuantas = ubicadas.length

    // Con un técnico escogido se habla de él; sin escoger, del día entero.
    // Antes esto último se callaba: un día sin trabajo dejaba el mapa en
    // blanco sin decir que estaba vacío porque no había nada, no porque
    // faltara algo por ubicar.
    if (tecnicoVista === 'todos') {
      if (cuantas === 0) return 'Ese día no hay nada programado'
      if (rutas.length === 0) return 'Ese día nadie tiene más de una parada: no hay recorridos'
      return ''
    }

    if (cuantas === 0) return 'No tiene trabajo programado ese día'
    if (cuantas === 1) return 'Ese día tiene una sola parada: no hay recorrido que dibujar'

    return ''
  }, [modo, tecnicoVista, diaVista, ubicadas, rutas])


  useEffect(() => {
    if (!hayLlaveDeGoogle()) {
      setError('Falta la llave de Google Maps para poder dibujar el mapa.')
      return
    }

    let vivo = true

    void cargarGoogleMaps()
      .then((api) => {
        if (!vivo || !caja.current) return
        const maps = window.google.maps
        const PuntoOrden = clasePunto(maps)

        if (!mapa.current) {
          mapa.current = new api.maps.Map(caja.current, {
            styles: ESTILO_MAPA,
            center: CARTAGO,
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            // LA RUEDA ACERCA, SIN PEDIR CTRL.
            //
            // Sin esta línea Google aplica su valor por defecto, que en una
            // página con desplazamiento exige Ctrl y tapa el mapa con un
            // cartel. En el mapa de calor sí tiene sentido lo contrario —vive
            // en una página larga y la rueda se quedaría atrapada— pero esta
            // pantalla llena la ventana: no hay página que bajar, así que el
            // cartel solo estorba a quien vino a mirar el mapa.
            gestureHandling: 'greedy',
          })
        }

        // El overlay que solo presta su proyección para soltar órdenes a mano.
        // Se cuelga una vez y se queda: no se toca en los redibujados.
        if (!proyeccion.current) {
          const Proyeccion = claseProyeccion(maps)
          const ov = new Proyeccion()
          ov.setMap(mapa.current)
          proyeccion.current = ov
        }

        // Se redibuja entero en cada cambio de filtro o de datos.
        marcas.current.forEach((m) => m.setMap(null))
        marcas.current = []
        lineas.current.forEach((l) => l.setMap(null))
        lineas.current = []

        // LAS RUTAS VAN PRIMERO, debajo de los puntos: la línea es el hilo y los
        // puntos las cuentas. Una flecha cada tanto dice hacia dónde va el día.
        for (const r of rutas) {
          const linea = new maps.Polyline({
            path: r.puntos,
            map: mapa.current,
            strokeColor: r.color,
            strokeOpacity: 0.85,
            strokeWeight: 3,
            icons: [
              {
                icon: {
                  path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 2.2,
                  strokeColor: r.color,
                  strokeWeight: 1,
                  fillColor: r.color,
                  fillOpacity: 1,
                },
                offset: '60%',
                repeat: '150px',
              },
            ],
          })
          lineas.current.push(linea)
        }

        const limites = new maps.LatLngBounds()

        for (const { orden, efectivo, lat, lon } of ubicadas) {
          const posicion = { lat: Number(lat), lng: Number(lon) }
          const color = COLOR[efectivo]

          // Solo late lo que está pasando AHORA MISMO. Si latiera todo, no
          // latiría nada: el latido es una llamada de atención y se gasta si
          // se reparte.
          const late = efectivo === 'en_ejecucion'

          const marca = new PuntoOrden(
            posicion,
            color,
            GLIFO[efectivo],
            late,
            `${orden.numero} · ${orden.cliente?.nombre ?? 'Sin cliente'}`,
          )
          marca.setMap(mapa.current)

          // Sin `onProgramar` el mapa solo se mira: ni ficha con botón, ni
          // punto que acepte a nadie.
          const puedeProgramar =
            Boolean(onProgramarRef.current) &&
            efectivo === 'pendiente_programacion' &&
            orden.orden_trabajo
          const ficha = new maps.InfoWindow({
            content: `
              <div style="font-family:Inter,system-ui,sans-serif;min-width:190px;line-height:1.45">
                <div style="font-weight:700;color:#2D0177;font-size:13px">${orden.numero}</div>
                <div style="font-weight:600;color:#180935;font-size:13px">${
                  orden.cliente?.nombre ?? 'Sin cliente'
                }</div>
                <div style="color:#47406B;font-size:12px">${orden.servicio?.nombre ?? ''}</div>
                <div style="color:#77709A;font-size:11.5px;margin-top:3px">${orden.direccion}</div>
                ${
                  orden.cotizacion?.valor_total
                    ? `<div style="margin-top:5px;font-weight:700;color:#2D0177;font-size:13px">${pesos(
                        orden.cotizacion.valor_total,
                      )}</div>`
                    : ''
                }
                <div style="margin-top:6px;font-size:11px;color:${color};font-weight:700;text-transform:uppercase">
                  ${ETIQUETA[efectivo]}
                </div>
                ${
                  puedeProgramar
                    ? `<button id="prog-${orden.id}" style="margin-top:9px;width:100%;padding:7px 10px;border:0;border-radius:7px;background:#0EA5E9;color:#fff;font-weight:700;font-size:12px;cursor:pointer">Programar</button>`
                    : ''
                }
              </div>`,
          })

          marca.alTocar(() => {
            ficha.setPosition(posicion)
            ficha.open({ map: mapa.current! })
            if (puedeProgramar) {
              // El botón vive en el HTML de la ficha; se engancha cuando el DOM
              // de la ficha ya existe.
              maps.event.addListenerOnce(ficha, 'domready', () => {
                const boton = document.getElementById(`prog-${orden.id}`)
                boton?.addEventListener('click', () => {
                  ficha.close()
                  onProgramarRef.current?.(orden)
                })
              })
            }
          })
          marca.alAbrir(() => onAbrirRef.current(orden.id))

          /*
           * MOVER EL PUNTO, SOLO EN EL MAPA GENERAL.
           *
           * Aquí es donde se ve que está mal puesto, así que aquí se corrige:
           * mandar a otra pantalla a arreglar lo que se está mirando es lo
           * que ya pasaba con las que no tenían punto.
           *
           * En rutas no: ahí el mapa cuenta por dónde anduvo alguien, y mover
           * una parada mientras se lee un recorrido es un gesto de otra tarea.
           */
          if (modoRef.current === 'general' && moviendoPuntos && !programando) {
            marca.alMover((lat, lon) => {
              const antes = { lat: Number(orden.latitud), lon: Number(orden.longitud) }
              void guardarRef.current(orden.id, lat, lon).then((bien) => {
                if (bien) setMovida({ id: orden.id, numero: orden.numero, ...antes })
              })
            })
          }

          // Solo las que de verdad pueden recibir a alguien.
          if (programando && puedeProgramarAqui && puedeProgramar) {
            marca.aceptarTecnico((colaboradorId) =>
              onProgramarRef.current?.(orden, colaboradorId, diaElegido),
            )
          }

          marcas.current.push(marca)
          limites.extend(posicion)
        }

        /*
         * EL ENCUADRE SE HACE CUANDO CAMBIA LO QUE SE MIRA, NO EN CADA
         * REFRESCO.
         *
         * Antes se reencuadraba siempre que se volvían a dibujar los puntos, y
         * eso incluye el refresco que sigue a mover uno a mano: Johan movía un
         * punto y «se achiquita todo el mapa y vuelve al punto de inicio».
         * Perder el zoom y el encuadre justo después de colocar algo es lo
         * contrario de lo que uno quiere; se acababa de acercar precisamente
         * para afinar.
         *
         * La firma dice qué se está mirando: el modo, el día, el técnico, el
         * filtro y cuántos puntos hay. Mover uno no cambia ninguna de esas
         * cosas, así que el mapa se queda donde está.
         */
        const firma = [modoRef.current, diaVista, tecnicoVista, filtro, ubicadas.length].join('·')

        if (encuadrado.current !== firma) {
          encuadrado.current = firma

          if (ubicadas.length > 1) {
            mapa.current.fitBounds(limites, 48)
          } else if (ubicadas.length === 1) {
            mapa.current.setCenter(limites.getCenter())
            mapa.current.setZoom(16)
          }
        }
      })
      .catch(() => setError('No se pudo cargar el mapa de Google.'))

    return () => {
      vivo = false
    }
    // `moviendoPuntos` entra en las dependencias a propósito: al encenderlo o
    // apagarlo hay que rehacer los puntos, que es donde se engancha —o no— el
    // arrastre.
  }, [ubicadas, programando, moviendoPuntos, diaElegido, rutas])

  /**
   * SOLTAR UNA ORDEN SIN UBICAR SOBRE EL MAPA.
   *
   * El sitio donde cae el cursor se convierte a latitud/longitud con la
   * proyección del overlay invisible, y ese punto se guarda como 'manual' —la
   * mejor fuente que hay, la que el geocodificador ya no pisa—. Al guardarse, la
   * orden pasa de la columna al mapa con su alfiler, sin recargar la pantalla.
   */
  function soltarOrden(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setArrastrando(false)

    const ordenId = e.dataTransfer.getData('text/orden-sin-ubicar')
    if (!ordenId) return

    const proj = proyeccion.current?.getProjection()
    const rect = caja.current?.getBoundingClientRect()
    if (!proj || !rect) return

    const maps = window.google.maps
    const pixel = new maps.Point(e.clientX - rect.left, e.clientY - rect.top)
    const punto = proj.fromContainerPixelToLatLng(pixel)
    if (!punto) return

    void guardar(ordenId, punto.lat(), punto.lng())
  }

  return (
    <div className={estilos.zona}>
      {error ? (
        <p className={estilos.error}>{error}</p>
      ) : (
        <>
          {/* EL FILTRO POR DÍA, ARRIBA DEL MAPA. Vacío = todos los días. Con un
              día puesto, el mapa muestra solo lo programado para esa fecha —el
              plan de esa mañana—, sin tocar la matriz ni la leyenda: se apilan. */}
          <div className={estilos.cabecera}>
            {/* De qué va este mapa. Dos, no uno con todo encima. */}
            <div className={estilos.modos} role="group" aria-label="Qué mostrar en el mapa">
              <button
                type="button"
                className={modo === 'general' ? estilos.modoActivo : estilos.modo}
                aria-pressed={modo === 'general'}
                onClick={() => cambiarModo('general')}
              >
                General
              </button>
              <button
                type="button"
                className={modo === 'rutas' ? estilos.modoActivo : estilos.modo}
                aria-pressed={modo === 'rutas'}
                onClick={() => cambiarModo('rutas')}
              >
                Rutas del día
              </button>
              <button
                type="button"
                className={modo === 'finalizados' ? estilos.modoActivo : estilos.modo}
                aria-pressed={modo === 'finalizados'}
                onClick={() => cambiarModo('finalizados')}
              >
                Finalizados
              </button>
            </div>

            {modo === 'rutas' && (
              <>
            <label className={estilos.diaRotulo} htmlFor="mapa-dia">
              Día
            </label>
            <input
              id="mapa-dia"
              type="date"
              className={estilos.diaVista}
              value={diaVista ?? ''}
              onChange={(e) => setDiaVista(e.target.value || null)}
              aria-label="Ver solo un día"
              title="Ver solo las órdenes programadas para un día"
            />

            {/* DE QUIÉN. El mapa general se queda como estaba —«Todos»— y al
                escoger un nombre queda solo su recorrido. La lista son los que
                tienen trabajo en lo que se está mirando, no la plantilla
                entera. */}
            {/* SIEMPRE PUESTO EN ESTE MODO, aunque ese día no haya nadie
                trabajando. Antes desaparecía al cambiar a un día vacío y
                dejaba al técnico escogido sin forma de cambiarlo ni de volver
                a «Todos»: el mapa se quedaba en blanco y el mando que lo
                explicaba ya no estaba. */}
            <label className={estilos.diaRotulo} htmlFor="mapa-tecnico">
              Técnico
            </label>
            <select
              id="mapa-tecnico"
              className={estilos.tecnicoVista}
              value={tecnicoVista}
              onChange={(e) => setTecnicoVista(e.target.value)}
              aria-label="Ver la ruta de un técnico"
            >
              <option value="todos">Todos</option>
              {tecnicosALaVista.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
              {/* El escogido, cuando ese día no tiene trabajo: sin esta opción
                  el desplegable no podría mostrar su propio valor. */}
              {tecnicoVista !== 'todos' &&
                !tecnicosALaVista.some((t) => t.id === tecnicoVista) && (
                  <option value={tecnicoVista}>
                    {nombrePorTecnico.get(tecnicoVista) ?? 'Técnico'}
                  </option>
                )}
            </select>

            {/* POR QUÉ NO SE VE LA RUTA, CUANDO NO SE VE.
                Un mapa vacío no dice si el técnico libró, si solo tuvo una
                parada o si falta escoger el día. Johan escogió a Yeison en un
                día en el que no tenía trabajo y lo único que pasó fue nada.
                Callarse es el fallo; el mapa vacío es solo el síntoma. */}
            {porQueNoHayRuta && <span className={estilos.pista}>{porQueNoHayRuta}</span>}

            {/* Cada técnico, con el color de su ruta: la línea del mapa dice de
                quién es el recorrido, y aquí se lee el nombre. */}
            {rutas.length > 0 && (
              <ul className={estilos.rutasLeyenda}>
                {rutas.map((r) => (
                  <li key={r.id}>
                    <span
                      className={estilos.rutaColor}
                      style={{ background: r.color }}
                      aria-hidden="true"
                    />
                    {r.nombre}
                  </li>
                ))}
              </ul>
            )}
              </>
            )}

            {/* MOVER PUNTOS, ARRIBA Y NO AL PIE.
                Estaba junto a «Programar sobre el mapa», en el pie del mapa,
                y Johan preguntó dónde salía: al pie está lo que se usa una vez
                al día, y esto se busca justo cuando se ve un punto mal puesto.
                Va con los modos, que es donde se mira primero. */}
            {modo === 'general' && !programando && (
              <button
                type="button"
                className={moviendoPuntos ? estilos.modoActivo : estilos.modo}
                aria-pressed={moviendoPuntos}
                onClick={() => setMoviendoPuntos((v) => !v)}
                title="Arrastrar los puntos para corregir dónde está cada orden"
              >
                {moviendoPuntos ? 'Salir de mover' : 'Mover puntos'}
              </button>
            )}

            {modo === 'general' &&
              (movida ? (
                /* DESHACER, QUE ES LO QUE QUITA EL MIEDO A ARRASTRAR.
                   Sin esto, mover un punto sin querer no tiene vuelta atrás
                   salvo buscando la orden y corrigiéndola a ojo. */
                <span className={estilos.pista}>
                  {movida.numero} quedó en el sitio nuevo.{' '}
                  <button
                    type="button"
                    className={estilos.limpiar}
                    onClick={() => {
                      void guardar(movida.id, movida.lat, movida.lon)
                      setMovida(null)
                    }}
                  >
                    Deshacer
                  </button>
                </span>
              ) : (
                <span className={estilos.pista}>
                  {moviendoPuntos
                    ? 'Arrastre cada punto a su sitio. Al terminar, salga de mover.'
                    : 'Dónde está cada trabajo y cómo va. Para ver por dónde anda un técnico, pase a «Rutas del día».'}
                </span>
              ))}

            {modo === 'finalizados' && (
              <span className={estilos.pista}>
                {ubicadas.length === 0
                  ? 'Todavía no hay servicios finalizados con punto en el mapa.'
                  : 'Los trabajos terminados y firmados, por dónde quedaron.'}
              </span>
            )}
          </div>

          <div className={estilos.lienzo}>
            {/* LA COLUMNA DE LAS QUE FALTAN.
                Cuando «ubicarlas en el mapa» no da con alguna —hay direcciones
                de finca y de sitio con nombre que Google no encuentra—, la orden
                se queda sin punto. Antes solo se contaba al pie («23 sin
                ubicar») y no había forma de arreglarla desde aquí: la salida
                vivía en otra pantalla. Ahora está en esta columna, y se arrastra
                al sitio exacto sobre el mapa, que es quien conoce Cartago.

                No sale mientras se programa: ahí la izquierda es de los técnicos,
                y ubicar es otra tarea. */}
            {modo === 'general' && !programando && sinUbicar.length > 0 && (
              <aside className={estilos.panelSinUbicar}>
                <div className={estilos.panelCab}>
                  <p className={estilos.panelTitulo}>Sin ubicar · {sinUbicar.length}</p>
                  <p className={estilos.panelAyuda}>
                    Google no dio con estas. Arrástralas al sitio exacto en el mapa.
                  </p>
                </div>

                <div className={estilos.listaSinUbicar}>
                  {sinUbicar.map(({ orden }) => (
                    <div
                      key={orden.id}
                      className={estilos.ordenSinUbicar}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/orden-sin-ubicar', orden.id)
                        e.dataTransfer.effectAllowed = 'copy'
                        setArrastrando(true)
                      }}
                      onDragEnd={() => setArrastrando(false)}
                      title={`${orden.numero} · ${orden.direccion}`}
                    >
                      <span className={estilos.ordenNum}>{orden.numero}</span>
                      <span className={estilos.ordenTxt}>
                        <b>{orden.cliente?.nombre ?? 'Sin cliente'}</b>
                        <i>{orden.direccion}</i>
                      </span>
                    </div>
                  ))}
                </div>

                {(guardando || errorUbicar) && (
                  <p className={estilos.panelPie}>
                    {guardando ? 'Guardando la ubicación…' : errorUbicar}
                  </p>
                )}
              </aside>
            )}

            <div className={estilos.envolturaMapa}>
              <div
                ref={caja}
                className={`${estilos.mapa} ${programando ? estilos.apagado : ''} ${
                  arrastrando ? estilos.recibiendo : ''
                }`}
                onDragOver={
                  arrastrando
                    ? (e) => {
                        e.preventDefault()
                        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
                      }
                    : undefined
                }
                onDrop={soltarOrden}
              />

              {/* CARTAGO EN BLANCO Y UNA LEYENDA AL PIE. Eso era el mapa con las
                  veintitrés órdenes sin geocodificar: la pantalla no decía qué
                  pasaba ni qué hacer, y lo que había que hacer vivía en otra
                  pantalla. Cuando no hay NI UN punto, ubicarlas es lo único que
                  cabe hacer aquí, así que va en el medio y no al pie.

                  PERO SOLO EN EL MAPA GENERAL. En las rutas del día, que no
                  haya puntos casi nunca es un problema de geocodificación:
                  es que ese día no trabajó nadie. Ofrecer ahí «ubicarlas»
                  manda a gastar consultas de Google contra un vacío que no
                  tiene nada que ver, y tapa el aviso que sí explica qué pasa.
                  Johan lo vio en cuanto abrió un día sin trabajo. */}
              {modo === 'general' && ubicadas.length === 0 && (
                <UbicarPendientes variante="panel" />
              )}
            </div>
          </div>

          {programando && (
            <aside className={estilos.panelTecnicos}>
              <div className={estilos.panelCab}>
                <p className={estilos.panelTitulo}>Arrastre a quien va</p>
                <input
                  type="date"
                  className={estilos.panelFecha}
                  value={diaElegido}
                  onChange={(e) => setDiaElegido(e.target.value)}
                  aria-label="Día que se está programando"
                />
              </div>

              <div className={estilos.listaTecnicos}>
                {cargaDelDia.map((c) => (
                  <div
                    key={c.id}
                    className={estilos.tecnico}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/colaborador', c.id)
                      e.dataTransfer.effectAllowed = 'copy'
                    }}
                    title={`${c.nombre} · ${c.horas} h ese día`}
                  >
                    <span className={estilos.tecnicoIni}>
                      {c.nombre
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((x) => x[0])
                        .join('')
                        .toUpperCase()}
                    </span>
                    <span className={estilos.tecnicoTxt}>
                      <b>{c.nombre}</b>
                      <i>{c.cargo ?? 'Sin cargo'}</i>
                    </span>
                    <span
                      className={`${estilos.tecnicoCarga} ${c.horas > 8 ? estilos.tecnicoLleno : ''}`}
                    >
                      {c.horas}h
                    </span>
                  </div>
                ))}
              </div>

              <p className={estilos.panelPie}>
                Solo los puntos con anillo aceptan técnico: son los aprobados sin
                programar. Al soltar se acuerda la hora.
              </p>
            </aside>
          )}

          <div className={estilos.pie}>
            {/* El interruptor del modo. Va aquí y no arriba porque pertenece al
                mapa, no a la pantalla: la Matriz tiene sus propios filtros. */}
            {onProgramar && !moviendoPuntos && (
              <button
                type="button"
                className={`${estilos.modo} ${programando ? estilos.modoVivo : ''}`}
                aria-pressed={programando}
                onClick={() => setProgramando((v) => !v)}
              >
                {programando ? 'Salir de programar' : 'Programar sobre el mapa'}
              </button>
            )}

            {/* La leyenda ES el filtro. Solo salen los estados que hay, y solo
                en el mapa general: en las rutas el estado del documento no
                dice nada de por dónde anduvo nadie. */}
            <ul className={estilos.leyenda} hidden={modo !== 'general'}>
              {ESTADOS.filter((e) => presentes.has(e.clave)).map(({ clave, etiqueta, color }) => {
                const activo = filtro === clave
                return (
                  <li key={clave}>
                    <button
                      type="button"
                      className={`${estilos.filtro} ${activo ? estilos.filtroActivo : ''}`}
                      aria-pressed={activo}
                      onClick={() => setFiltro(activo ? 'todos' : clave)}
                      title={activo ? 'Quitar el filtro' : 'Ver solo estas'}
                    >
                      <span className={estilos.punto} style={{ background: color }} aria-hidden="true" />
                      {etiqueta}
                    </button>
                  </li>
                )
              })}
              {filtro !== 'todos' && (
                <li>
                  <button type="button" className={estilos.limpiar} onClick={() => setFiltro('todos')}>
                    Ver todas
                  </button>
                </li>
              )}
            </ul>

            <span className={estilos.cuenta}>
              {ubicadas.length} en el mapa
              {sinUbicar.length > 0 && (
                <>
                  {' · '}
                  <b title={sinUbicar.map((o) => o.orden.numero).join(', ')}>
                    {sinUbicar.length} sin ubicar
                  </b>
                </>
              )}
              {' · doble clic para abrir'}
            </span>

            {/* «23 sin ubicar» era una etiqueta muerta: decía el problema y no
                daba la salida, que vivía en otra pantalla. Ahora la salida está
                donde se ve el vacío. Tampoco en rutas: una orden sin punto no
                es un problema del recorrido de nadie. */}
            {modo === 'general' && ubicadas.length > 0 && <UbicarPendientes />}
          </div>
        </>
      )}
    </div>
  )
}
